import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchAllCustomers } from '../apiRequests/api';
import { SigninContext } from '../contexts/SigninContext';
import Loading from './Loading';
import ReactPaginate from 'react-paginate';
import '../sass/components/admin.scss';
import '../sass/components/forms.scss';
import '../sass/components/pagingation.scss';

export default function OrderForCustomer() {
    const settings = useAppSettings();
    const history = useHistory();
    const { getIdTokenClaims } = useAuth0();
    const { setUserName, setCustomerName } = useContext(SigninContext);
    const [allCustomers, setAllCustomers] = useState([]);
    const [isLoadingBatches, setIsLoadingBatches] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 10 });
    const [customersError, setCustomersError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const customersPerPage = 20;
    const totalBatches = 10;

    useEffect(() => {
        if (!settings['__empty'] && !settings['__is_admin_user']) {
            history.push("/product-list");
        }
    }, [settings, history]);

    // Load customers from sessionStorage on mount
    useEffect(() => {
        const cachedCustomers = sessionStorage.getItem('allTradeCustomers');
        if (cachedCustomers) {
            try {
                const parsedCustomers = JSON.parse(cachedCustomers);
                setAllCustomers(parsedCustomers);
            } catch (error) {
                console.error('Error parsing cached customers:', error);
                sessionStorage.removeItem('allTradeCustomers');
            }
        }
    }, []);

    const handleFetchAllCustomers = async () => {
        setIsLoadingBatches(true);
        setCustomersError(null);
        setAllCustomers([]);
        setCurrentPage(1);

        try {
            const token = await getIdTokenClaims();
            const mergedCustomers = [];

            // Fetch all batches sequentially
            for (let batchNum = 1; batchNum <= totalBatches; batchNum++) {
                setLoadingProgress({ current: batchNum, total: totalBatches });

                try {
                    const response = await fetchAllCustomers({ batch: batchNum }, token.__raw);

                    if (response.status === 'success') {
                        // Merge customers into single array
                        mergedCustomers.push(...(response.customers || []));
                    } else {
                        console.error(`Batch ${batchNum} failed:`, response.message);
                    }
                } catch (batchError) {
                    console.error(`Error fetching batch ${batchNum}:`, batchError);
                }
            }

            // Only update state after all batches are complete
            setAllCustomers(mergedCustomers);

            // Save to sessionStorage after all batches are loaded
            if (mergedCustomers.length > 0) {
                sessionStorage.setItem('allTradeCustomers', JSON.stringify(mergedCustomers));
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomersError(error.message || 'Failed to load customers');
        } finally {
            setIsLoadingBatches(false);
            setLoadingProgress({ current: 0, total: 10 });
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Filter customers based on search term
    const filteredCustomers = allCustomers.filter(
        customer => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        const accountMatch = customer.CustomerAccount?.toLowerCase().includes(searchLower);
        const emailMatch = customer.Email?.toLowerCase().includes(searchLower);
        const nameMatch = customer.Billing_Name?.toLowerCase().includes(searchLower);

        return accountMatch || emailMatch || nameMatch;
    });

    // Calculate pagination
    const indexOfLastCustomer = currentPage * customersPerPage;
    const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
    const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
    const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

    const handlePageClick = (paginationData) => {
        setCurrentPage(paginationData.selected + 1);
        // Scroll to top of results
        document.querySelector('.customers-table-container')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleCustomerRowClick = async (customer) => {
        try {
            // Extract the customer name
            const customerName = customer.Billing_Name ||
                              customer.CustomerName ||
                              customer.CustomerAccount;

            // Set the user account and customer name in the context
            setUserName(customer.CustomerAccount);

            // Save to session storage for persistence
            if (customerName) {
                sessionStorage.setItem('customerName', customerName);
            }

            // Set the customer name in the context
            if (setCustomerName) {
                setCustomerName(customerName);
            }

            // Force a small delay to ensure state updates propagate
            await new Promise(resolve => setTimeout(resolve, 100));

            // Redirect to product list
            history.push('/product-list');
        } catch (error) {
            console.error('Error logging in as customer:', error);
            setCustomersError('Failed to switch to customer account. Please try again.');
        }
    };

    if (settings['__empty']) {
        return (
            <div className='product-list-wrapper'>
                <h1 className='page-title'>Order for Customer</h1>
                <Loading isLoading={true} />
            </div>
        );
    }

    if (!settings['__is_admin_user']) {
        return null; // Will be redirected by the useEffect
    }

    return (
        <div className="admin order-for-customer">
            <div className="admin__form">
                <div className="section">
                    <h2 className="page-title">Customer Lookup</h2>

                    <div className="form-container">
                        <div className="form-column" style={{ width: '100%', maxWidth: 'none' }}>
                            {allCustomers.length === 0 ? (
                                // No customers loaded - show centered button only
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleFetchAllCustomers}
                                        disabled={isLoadingBatches}
                                        style={{ minWidth: '200px' }}
                                    >
                                        {isLoadingBatches
                                            ? `Loading... (${loadingProgress.current}/${loadingProgress.total})`
                                            : 'Load All Customers'
                                        }
                                    </button>
                                </div>
                            ) : (
                                // Customers loaded - show search field and clear button
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                                    <div className="form-group" style={{ flex: '0 0 75%', marginBottom: 0 }}>
                                        <label htmlFor="customerSearch">Filter Results</label>
                                        <input
                                            type="text"
                                            id="customerSearch"
                                            name="customerSearch"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            placeholder="Search by account, email, or name..."
                                        />
                                    </div>
                                    <div className="form-actions" style={{ flex: '0 0 calc(25% - 15px)', marginBottom: 0 }}>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleClearFilters}
                                            disabled={!searchTerm}
                                            style={{
                                                width: '100%',
                                                opacity: !searchTerm ? 0.5 : 1,
                                                cursor: !searchTerm ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {customersError && (
                    <div className="section" style={{ marginTop: '20px' }}>
                        <div className="error-message" style={{ color: '#e74c3c', padding: '15px', backgroundColor: '#fee', borderRadius: '4px' }}>
                            {customersError}
                        </div>
                    </div>
                )}

                {allCustomers.length > 0 && (
                    <div className="section" style={{ marginTop: '20px' }}>
                        <div className="form-container">
                            <div className="form-column" style={{ width: '100%', maxWidth: 'none' }}>
                                <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                                    {searchTerm ? (
                                        <>
                                            Found <strong>{filteredCustomers.length}</strong> of <strong>{allCustomers.length}</strong> customers matching "{searchTerm}".
                                            {totalPages > 1 && ` Showing ${indexOfFirstCustomer + 1}-${Math.min(indexOfLastCustomer, filteredCustomers.length)}.`}
                                        </>
                                    ) : (
                                        <>
                                            Found <strong>{allCustomers.length}</strong> customers.
                                            {totalPages > 1 && ` Showing ${indexOfFirstCustomer + 1}-${Math.min(indexOfLastCustomer, allCustomers.length)}.`}
                                        </>
                                    )}
                                </p>

                                {filteredCustomers.length === 0 ? (
                                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#999', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                                        <p>No customers match your search criteria.</p>
                                        <p style={{ fontSize: '14px', marginTop: '10px' }}>Try adjusting your filter.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="customers-table-container">
                                            <table className="customers-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
                                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Customer Account</th>
                                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                                                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Billing Name</th>
                                                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', width: '100px' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentCustomers.map((customer, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{
                                                                borderBottom: '1px solid #eee',
                                                                transition: 'background-color 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                        >
                                                            <td style={{ padding: '12px', fontWeight: '500' }}>{customer.CustomerAccount || 'N/A'}</td>
                                                            <td style={{ padding: '12px' }}>{customer.Email || 'N/A'}</td>
                                                            <td style={{ padding: '12px' }}>{customer.Billing_Name || 'N/A'}</td>
                                                            <td style={{ padding: '12px', textAlign: 'center' }}>
                                                                <button
                                                                    className="login-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCustomerRowClick(customer);
                                                                    }}
                                                                    style={{ width: 'auto', padding: '8px 20px' }}
                                                                    title="Login as Customer"
                                                                >
                                                                    <i className="fas fa-arrow-right-to-bracket"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalPages > 1 && (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                marginTop: '20px',
                                                paddingTop: '20px',
                                                borderTop: '1px solid #eee'
                                            }}>
                                                <ReactPaginate
                                                    forcePage={currentPage - 1}
                                                    pageCount={totalPages}
                                                    previousLabel={'previous'}
                                                    previousClassName={'previous'}
                                                    nextLabel={'next'}
                                                    nextClassName={'next'}
                                                    onPageChange={handlePageClick}
                                                    containerClassName={'pagination'}
                                                    activeClassName={'active'}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
